import React, { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { Play, Film, DoorOpen, BookUser, CalendarDays } from "lucide-react";

import Header from "./Header";
import PanoramaViewer from "./PanoramaViewer";
import IsoViewer from "./IsoViewer";
import PortalsOverlay from "./PortalsOverlay";
import ComingSoon from "./ComingSoon";
import UploadPanel from "./UploadPanel";
import VideoPlayer from "./VideoPlayer";
import AudioPlayer from "./AudioPlayer";
import PhotoGallery from "./PhotoGallery";
import SplashScreen from "./SplashScreen";
import LoginScreen from "./LoginScreen";
import BioPanel from "./BioPanel";
import EventCalendar from "./EventCalendar";
import AdminPanel from "./AdminPanel";
import MerchStore from "./MerchStore";
import { useAdmin } from "../contexts/AdminContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VIEW_LABELS = {
    front: "Front Atrium",
    rear: "Rear Atrium",
    hallway: "Hallway",
    interior: "Interior",
};

export default function CaprioChateau() {
    const [sections, setSections] = useState([]);
    const [appConfig, setAppConfig] = useState({ splash_video_url: "", splash_title: "Drive into Caprio Chateau" });
    // Read the initial tab from the URL path (e.g. /caprio-vibe-tv) so
    // promotional links deep-link straight into the right tab.
    const initialTab = (() => {
        try {
            const slug = (window.location.pathname || "/").replace(/^\/+|\/+$/g, "").toLowerCase();
            if (slug && /^[a-z0-9-]+$/.test(slug)) return slug;
        } catch (_e) { /* noop */ }
        return "center-hub";
    })();
    const [currentTab, setCurrentTabRaw] = useState(initialTab);

    // Update tab AND browser URL so back/forward + share links work.
    const setCurrentTab = (id) => {
        setCurrentTabRaw(id);
        try {
            const path = id && id !== "center-hub" ? `/${id}` : "/";
            if (window.location.pathname !== path) {
                window.history.pushState({ tab: id }, "", path);
            }
        } catch (_e) { /* noop */ }
    };

    // Keep browser back/forward in sync.
    useEffect(() => {
        const onPop = () => {
            const slug = (window.location.pathname || "/").replace(/^\/+|\/+$/g, "").toLowerCase();
            setCurrentTabRaw(slug && /^[a-z0-9-]+$/.test(slug) ? slug : "center-hub");
        };
        window.addEventListener("popstate", onPop);
        return () => window.removeEventListener("popstate", onPop);
    }, []);

    // Once sections load, if the URL slug isn't a real section, fall back to center-hub.
    useEffect(() => {
        if (!sections.length) return;
        const ids = new Set(sections.map((s) => s.id));
        ids.add("portals");
        if (!ids.has(currentTab)) {
            setCurrentTabRaw("center-hub");
            try { window.history.replaceState({}, "", "/"); } catch (_e) { /* noop */ }
        }
    }, [sections, currentTab]);
    const [isNight, setIsNight] = useState(true);
    const [isIso, setIsIso] = useState(false);
    const [subView, setSubView] = useState({});
    const [loading, setLoading] = useState(true);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploadInitialView, setUploadInitialView] = useState(null);
    const [videoPlayerOpen, setVideoPlayerOpen] = useState(false);
    const [menuHidden, setMenuHidden] = useState(false);
    const [adminPanelOpen, setAdminPanelOpen] = useState(false);
    const [audioBarCollapsed, setAudioBarCollapsed] = useState(false);
    const [splashDone, setSplashDone] = useState(() => {
        try { return sessionStorage.getItem("caprio_splash_done") === "1"; } catch (_) { return false; }
    });
    const { isAdmin, setGateOpen } = useAdmin();

    // Toggle popups (bio for HG, events for Garage)
    const [bioOpen, setBioOpen] = useState(false);
    const [eventsOpen, setEventsOpen] = useState(false);

    // 4-click gesture on Hallway / Interior buttons → open upload modal pre-focused on that view.
    const clickTrackerRef = useRef({ key: null, count: 0, timer: null });
    const [secretCount, setSecretCount] = useState({ key: null, count: 0 });

    const handleViewClick = (v) => {
        // Always switch view first
        setSubView((prev) => ({ ...prev, [currentTab]: v }));

        if (activeSection?.kind !== "hallway") return;
        // Admin-only secret gesture
        if (!isAdmin) return;

        const key = `${currentTab}:${v}`;
        const tracker = clickTrackerRef.current;
        if (tracker.timer) clearTimeout(tracker.timer);

        const count = tracker.key === key ? tracker.count + 1 : 1;
        tracker.key = key;
        tracker.count = count;
        setSecretCount({ key, count });

        if (count >= 4) {
            tracker.key = null;
            tracker.count = 0;
            setSecretCount({ key: null, count: 0 });
            setUploadInitialView(v);
            setUploadOpen(true);
            return;
        }
        tracker.timer = setTimeout(() => {
            clickTrackerRef.current = { key: null, count: 0, timer: null };
            setSecretCount({ key: null, count: 0 });
        }, 1500);
    };

    // Fetch sections + config
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const [sec, cfg] = await Promise.all([
                    axios.get(`${API}/sections`),
                    axios.get(`${API}/config`),
                ]);
                if (mounted) {
                    setSections(sec.data);
                    setAppConfig(cfg.data);
                }
            } catch (e) {
                console.error("Failed to load", e);
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, []);

    const onSplashComplete = () => {
        try { sessionStorage.setItem("caprio_splash_done", "1"); } catch (_) {}
        setSplashDone(true);
        // After the intro video (or Skip), send viewers straight to Caprio Vibe TV.
        // Only respect a real deep-link slug so promo URLs still work.
        try {
            const slug = (window.location.pathname || "/").replace(/^\/+|\/+$/g, "").toLowerCase();
            if (!slug || slug === "center-hub") {
                setCurrentTab("caprio-vibe-tv");
            }
        } catch (_e) {
            setCurrentTab("caprio-vibe-tv");
        }
    };

    const sectionById = useMemo(() => {
        const m = {};
        sections.forEach((s) => { m[s.id] = s; });
        return m;
    }, [sections]);

    const activeSection = sectionById[currentTab];
    const isPortalsTab = currentTab === "portals";
    const mode = isNight ? "night" : "day";

    // Default sub-view per section kind
    const defaultView = (s) => {
        if (!s) return "front";
        if (s.kind === "center") return "front";
        if (s.kind === "hallway") return "hallway";
        return "front";
    };

    const currentView = subView[currentTab] || defaultView(activeSection);

    // Reset video modal when changing section
    useEffect(() => {
        setVideoPlayerOpen(false);
        setUploadOpen(false);
        setUploadInitialView(null);
        setBioOpen(false);
        setEventsOpen(false);
        // also reset secret-click tracker
        if (clickTrackerRef.current.timer) clearTimeout(clickTrackerRef.current.timer);
        clickTrackerRef.current = { key: null, count: 0, timer: null };
        setSecretCount({ key: null, count: 0 });
    }, [currentTab]);

    // Image to render
    const fallbackImage = isIso
        ? sectionById["center-hub"]?.iso?.[mode]
        : sectionById["center-hub"]?.panoramas?.["front"]?.[mode];

    const activeImage = (() => {
        if (!activeSection || isPortalsTab) return fallbackImage;
        if (isIso) return activeSection.iso?.[mode];
        const pano = activeSection.panoramas?.[currentView]?.[mode];
        if (pano) return pano;
        const anyView = Object.keys(activeSection.panoramas || {})[0];
        return anyView ? activeSection.panoramas[anyView]?.[mode] : null;
    })();

    const hasContent = (() => {
        if (!activeSection) return false;
        if (isIso) return !!activeSection.iso?.[mode];
        return !!activeSection.panoramas?.[currentView]?.[mode];
    })();

    // Hotspots filtered to the current view
    const activeHotspots = (activeSection?.hotspots || []).filter(
        (h) => h.view === currentView
    );

    // Hotspot click handler
    const onHotspotClick = (target) => {
        if (target === "video_playlist") {
            setVideoPlayerOpen(true);
            return;
        }
        if (target === "photo_gallery" || target === "audio_playlist") {
            return; // photo gallery / audio bar are visible by default
        }
        // Otherwise switch to another panorama view in the same section
        setSubView((prev) => ({ ...prev, [currentTab]: target }));
    };

    const onSectionUpdated = (updated) => {
        setSections((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    };

    const accent = isNight ? "#D4AF37" : "#B89B5E";

    const feature = activeSection?.feature;
    const kind = activeSection?.kind;
    const isCinemaPod = feature === "cinema";
    const isGalleryPod = feature === "gallery";
    const isMusicPod = feature === "music";
    const isSingleVideoPod = feature === "single_video";
    const isBioFeature = feature === "bio";
    const isEventsFeature = feature === "events";
    const isIframeKind = kind === "iframe";
    const isMerchKind = kind === "merch";
    // login is still fullscreen (login screen takes over)
    const isFullscreenFeature = feature === "login" || isIframeKind || isMerchKind;
    const showMediaActions = !isIso && !isPortalsTab && hasContent && !isFullscreenFeature;
    // Whether the AudioPlayer will render below — used to lift the view switch above it.
    // When collapsed to a tiny pill the layout drops back to normal.
    const audioBarShowing = !isIso && !isPortalsTab && !isFullscreenFeature && !audioBarCollapsed && (
        appConfig?.music_bar_p7_only
            ? isMusicPod
            : (isMusicPod || (activeSection?.media?.audio?.length > 0) || isAdmin)
    );

    return (
        <div
            data-testid="caprio-chateau-root"
            className="relative w-screen h-screen overflow-hidden"
            style={{
                background: isNight ? "#020207" : "#efe9da",
                color: isNight ? "#fff" : "#0a0a0a",
            }}
        >
            {/* Splash screen — shown on first session entry */}
            {!loading && !splashDone && (
                <SplashScreen
                    config={appConfig}
                    onEnter={onSplashComplete}
                    onConfigUpdated={setAppConfig}
                />
            )}
            {/* Background layer */}
            <div className="absolute inset-0 vignette" data-testid="background-layer">
                {loading ? (
                    <div className="w-full h-full grid place-items-center">
                        <div className="text-[10px] tracking-[0.5em] uppercase" style={{ color: accent }}>
                            Entering the Chateau…
                        </div>
                    </div>
                ) : !isPortalsTab && hasContent && activeImage ? (
                    isIso ? (
                        <IsoViewer imageUrl={activeImage} isNight={isNight} />
                    ) : (
                        <PanoramaViewer
                            imageUrl={activeImage}
                            isNight={isNight}
                            hotspots={activeHotspots}
                            onHotspotClick={onHotspotClick}
                        />
                    )
                ) : isPortalsTab && fallbackImage ? (
                    <div
                        className="absolute inset-0"
                        style={{
                            backgroundImage: `url(${fallbackImage})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            filter: "blur(18px) brightness(0.55) saturate(1.1)",
                            transform: "scale(1.1)",
                        }}
                    />
                ) : (
                    fallbackImage && (
                        <div
                            className="absolute inset-0"
                            style={{
                                backgroundImage: `url(${fallbackImage})`,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                                filter: "blur(22px) brightness(0.45) saturate(1.15)",
                                transform: "scale(1.1)",
                            }}
                        />
                    )
                )}
            </div>

            {/* Header */}
            {!loading && (
                <Header
                    sections={sections}
                    currentTab={currentTab}
                    onTabChange={(id) => setCurrentTab(id)}
                    isNight={isNight}
                    onToggleDayNight={() => setIsNight((v) => !v)}
                    isIso={isIso}
                    onToggleIso={() => setIsIso((v) => !v)}
                    onOpenUpload={() => { setUploadInitialView(null); setUploadOpen(true); }}
                    menuHidden={menuHidden}
                    onToggleMenu={() => setMenuHidden((v) => !v)}
                    onOpenAdminPanel={() => setAdminPanelOpen(true)}
                />
            )}

            {/* Sub-view switch (Center: Front/Rear · Hallway pods: Hallway/Interior)
                — always kept above the audio player and section caption. */}
            {!loading && !isIso && !isPortalsTab && !isFullscreenFeature && activeSection && (activeSection.kind === "center" || activeSection.kind === "hallway") && (
                <div
                    data-testid="view-switch"
                    className={`absolute z-[35] left-1/2 -translate-x-1/2 pointer-events-auto ${audioBarShowing ? "bottom-72 md:bottom-56" : "bottom-24 md:bottom-28"}`}
                >
                    <div className={`${isNight ? "glass" : "glass-day"} rounded-full p-1 flex items-center gap-1`}>
                        {(activeSection.kind === "center"
                            ? ["front", "rear"]
                            : ["hallway", "interior"]
                        ).map((v) => {
                            const active = currentView === v;
                            const trackKey = `${currentTab}:${v}`;
                            const dots = secretCount.key === trackKey ? secretCount.count : 0;
                            const isHallwayPod = activeSection.kind === "hallway";
                            return (
                                <button
                                    key={v}
                                    data-testid={`view-${v}`}
                                    onClick={() => isHallwayPod ? handleViewClick(v) : setSubView((prev) => ({ ...prev, [currentTab]: v }))}
                                    className="relative px-4 py-2 rounded-full text-[10px] tracking-[0.3em] uppercase transition-all flex items-center gap-1"
                                    style={{
                                        background: active ? accent : "transparent",
                                        color: active ? "#0a0a0a" : (isNight ? "#fff" : "#0a0a0a"),
                                    }}
                                    title={isHallwayPod ? "Click 4× to replace images" : undefined}
                                >
                                    {v === "hallway" && <DoorOpen size={11} />}
                                    {VIEW_LABELS[v] || v}
                                    {dots > 0 && (
                                        <span
                                            data-testid={`view-secret-counter-${v}`}
                                            className="absolute -top-2 -right-2 flex items-center gap-[2px] px-1.5 py-[2px] rounded-full"
                                            style={{
                                                background: "#0a0a0a",
                                                border: `1px solid ${accent}`,
                                                boxShadow: `0 0 12px ${accent}88`,
                                            }}
                                        >
                                            {[0, 1, 2, 3].map((i) => (
                                                <span
                                                    key={i}
                                                    className="block rounded-full"
                                                    style={{
                                                        width: 4,
                                                        height: 4,
                                                        background: i < dots ? accent : "rgba(255,255,255,0.18)",
                                                    }}
                                                />
                                            ))}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Feature action buttons (Watch / Music / Gallery / Cinema / Bio / Events) */}
            {!loading && showMediaActions && currentView !== "hallway" && (
                <div
                    data-testid="feature-actions"
                    className="absolute z-30 top-28 left-6 md:left-12 pointer-events-auto flex items-center gap-2"
                >
                    {isCinemaPod && (
                        <ActionPill
                            testid="open-cinema"
                            onClick={() => setVideoPlayerOpen(true)}
                            accent={accent}
                            isNight={isNight}
                            icon={<Film size={14} />}
                            label={`Cinema · ${activeSection?.media?.videos?.length || 0}`}
                        />
                    )}
                    {isSingleVideoPod && (
                        <ActionPill
                            testid="open-video"
                            onClick={() => setVideoPlayerOpen(true)}
                            accent={accent}
                            isNight={isNight}
                            icon={<Play size={14} />}
                            label={(activeSection?.media?.videos?.[0]) ? "Watch Now" : "Add Video"}
                        />
                    )}
                    {isBioFeature && (
                        <ActionPill
                            testid="toggle-bio"
                            onClick={() => setBioOpen((v) => !v)}
                            accent={accent}
                            isNight={isNight}
                            icon={<BookUser size={14} />}
                            label={bioOpen ? "Hide Bio" : "Captain's Bio"}
                            active={bioOpen}
                        />
                    )}
                    {isEventsFeature && (
                        <ActionPill
                            testid="toggle-events"
                            onClick={() => setEventsOpen((v) => !v)}
                            accent={accent}
                            isNight={isNight}
                            icon={<CalendarDays size={14} />}
                            label={eventsOpen ? "Hide Calendar" : `Events · ${activeSection?.events?.length || 0}`}
                            active={eventsOpen}
                        />
                    )}
                </div>
            )}

            {/* Audio player — visible on P7 (Recording Studio) always, on any
                other pod that has audio tracks, and for admins on every non-
                fullscreen tab so they can add the first track anywhere.
                Admin can force "P7 only" via config toggle. */}
            {!loading && !isIso && !isPortalsTab && !isFullscreenFeature && (
                appConfig?.music_bar_p7_only
                    ? isMusicPod
                    : (isMusicPod || (activeSection?.media?.audio?.length > 0) || isAdmin)
            ) && (
                <AudioPlayer
                    section={activeSection}
                    isNight={isNight}
                    onUpdated={onSectionUpdated}
                    onCollapsedChange={setAudioBarCollapsed}
                />
            )}

            {/* Photo gallery dropdown (P5/P6) — visible when not in hallway */}
            {!loading && showMediaActions && isGalleryPod && currentView === "interior" && (
                <PhotoGallery
                    section={activeSection}
                    isNight={isNight}
                    onUpdated={onSectionUpdated}
                />
            )}

            {/* Scrim + caption */}
            {!loading && !isPortalsTab && !isFullscreenFeature && activeSection && (
                <div
                    className="absolute z-10 bottom-0 left-0 w-full md:w-2/3 h-64 pointer-events-none"
                    style={{
                        background: isNight
                            ? "linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.0) 100%)"
                            : "linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.0) 100%)",
                    }}
                />
            )}
            {!loading && !isPortalsTab && !isFullscreenFeature && activeSection && (
                <div
                    data-testid="section-caption"
                    className={`absolute z-20 left-4 sm:left-8 md:left-12 right-4 md:right-auto max-w-md pointer-events-none fade-in ${audioBarShowing ? "bottom-80 md:bottom-64" : "bottom-40 md:bottom-8"}`}
                >
                    <div
                        className="text-[10px] tracking-[0.5em] uppercase mb-2"
                        style={{ color: accent, textShadow: "0 1px 6px rgba(0,0,0,0.55)" }}
                    >
                        {isIso
                            ? "ISO · Aerial Map"
                            : `360° · ${VIEW_LABELS[currentView] || currentView}`} · {isNight ? "Night" : "Day"}
                    </div>
                    <h1
                        className="font-serif-display text-3xl sm:text-4xl md:text-6xl uppercase tracking-luxe leading-none"
                        style={{
                            color: "#fff",
                            textShadow: "0 4px 28px rgba(0,0,0,0.8), 0 1px 4px rgba(0,0,0,0.7)",
                        }}
                    >
                        {activeSection.custom_label || activeSection.label}
                    </h1>
                    {activeSection.description && (
                        <p
                            className="mt-3 text-sm font-light tracking-wide max-w-sm"
                            style={{ color: "rgba(255,255,255,0.88)", textShadow: "0 1px 8px rgba(0,0,0,0.75)" }}
                        >
                            {activeSection.description}
                        </p>
                    )}
                </div>
            )}

            {/* Coming Soon overlay */}
            {!loading && !isPortalsTab && activeSection && !hasContent && !isFullscreenFeature && (
                <ComingSoon
                    section={activeSection}
                    isNight={isNight}
                    isIso={isIso}
                    onUpdated={onSectionUpdated}
                />
            )}

            {/* Portals overlay */}
            {!loading && isPortalsTab && (
                <PortalsOverlay
                    sections={sections}
                    onSelect={(id) => setCurrentTab(id)}
                    isNight={isNight}
                    onSectionUpdated={onSectionUpdated}
                    onReturnToSplash={() => {
                        try { sessionStorage.removeItem("caprio_splash_done"); } catch (_) {}
                        setSplashDone(false);
                        setCurrentTab("center-hub");
                    }}
                />
            )}

            {/* Login screen (Log-In tab) */}
            {!loading && currentTab === "login" && activeSection && (
                <LoginScreen
                    section={activeSection}
                    onUpdated={onSectionUpdated}
                    onBackToHub={() => setCurrentTab("center-hub")}
                />
            )}

            {/* Caprio Vibe TV — external app embedded via iframe */}
            {!loading && isIframeKind && activeSection?.iframe_url && (
                <iframe
                    key={activeSection.id + "|" + activeSection.iframe_url}
                    src={activeSection.iframe_url}
                    title={activeSection.custom_label || activeSection.label}
                    data-testid={`iframe-${activeSection.id}`}
                    className="absolute inset-0 z-30 w-full h-full border-0"
                    style={{ background: "#000" }}
                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                />
            )}
            {!loading && isIframeKind && !activeSection?.iframe_url && (
                <div className="absolute inset-0 z-30 grid place-items-center px-6 text-center"
                     style={{ background: "rgba(0,0,0,0.85)" }}>
                    <div>
                        <div className="text-[10px] tracking-[0.5em] uppercase mb-3" style={{ color: accent }}>
                            {activeSection.custom_label || activeSection.label}
                        </div>
                        <div className="font-serif-display text-4xl uppercase" style={{ color: "#fff" }}>
                            Coming Soon
                        </div>
                        <p className="mt-3 text-sm opacity-70" style={{ color: "#fff" }}>
                            Admin: open the Admin Panel and set the embed URL for this tab.
                        </p>
                    </div>
                </div>
            )}

            {/* Merch Store (Shopify) */}
            {!loading && isMerchKind && activeSection && (
                <MerchStore
                    section={activeSection}
                    isNight={isNight}
                    onUpdated={onSectionUpdated}
                />
            )}

            {/* HG Sea Vessel bio popup (toggle) */}
            {!loading && isBioFeature && bioOpen && (
                <BioPanel
                    section={activeSection}
                    isNight={isNight}
                    onUpdated={onSectionUpdated}
                    onClose={() => setBioOpen(false)}
                    asPopup
                />
            )}

            {/* Garage events popup (toggle) */}
            {!loading && isEventsFeature && eventsOpen && (
                <EventCalendar
                    section={activeSection}
                    isNight={isNight}
                    onUpdated={onSectionUpdated}
                    onClose={() => setEventsOpen(false)}
                    asPopup
                />
            )}

            {/* Upload modal */}
            {!loading && uploadOpen && activeSection && !isPortalsTab && (
                <div
                    data-testid="upload-modal"
                    className="absolute inset-0 z-50 flex items-center justify-center px-4 py-24 overflow-y-auto"
                    style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(8px)" }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setUploadOpen(false);
                            setUploadInitialView(null);
                        }
                    }}
                >
                    <UploadPanel
                        section={activeSection}
                        isNight={isNight}
                        onUpdated={onSectionUpdated}
                        onClose={() => { setUploadOpen(false); setUploadInitialView(null); }}
                        initialView={uploadInitialView}
                    />
                </div>
            )}

            {/* Video player modal (Cinema P4 / Single video P8) */}
            {!loading && videoPlayerOpen && activeSection && (
                <VideoPlayer
                    section={activeSection}
                    isNight={isNight}
                    onClose={() => setVideoPlayerOpen(false)}
                    onUpdated={onSectionUpdated}
                />
            )}

            {/* Centralized Admin Panel — opened via silent 5-click gesture on the logo. */}
            {!loading && isAdmin && (
                <AdminPanel
                    open={adminPanelOpen}
                    onClose={() => setAdminPanelOpen(false)}
                    sections={sections}
                    appConfig={appConfig}
                    onSectionUpdated={onSectionUpdated}
                    onConfigUpdated={setAppConfig}
                    onResetAll={async () => {
                        if (!window.confirm("Reset ALL sections back to defaults? This wipes your customizations.")) return;
                        try {
                            await axios.post(`${API}/sections/reset`);
                            const sec = await axios.get(`${API}/sections`);
                            setSections(sec.data);
                        } catch (e) { console.error(e); }
                    }}
                    onUpscaleAll={async () => {
                        try {
                            await axios.post(`${API}/sections/upscale-all`);
                            const sec = await axios.get(`${API}/sections`);
                            setSections(sec.data);
                        } catch (e) { console.error(e); }
                    }}
                />
            )}

            {/* Grain */}
            <div className="absolute inset-0 grain pointer-events-none z-40" />
        </div>
    );
}

function ActionPill({ testid, onClick, accent, isNight, icon, label, active = false }) {
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={onClick}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[10px] tracking-[0.3em] uppercase transition-all hover:scale-[1.03] ${isNight ? "glass" : "glass-day"}`}
            style={{
                border: `1px solid ${active ? accent : accent + "65"}`,
                color: active ? "#0a0a0a" : (isNight ? "#fff" : "#0a0a0a"),
                background: active ? accent : undefined,
                boxShadow: active ? `0 8px 22px -10px ${accent}` : undefined,
            }}
        >
            <span style={{ color: active ? "#0a0a0a" : accent }}>{icon}</span>
            {label}
        </button>
    );
}
